import ibaneraAccountTypeImage from 'assets/ibanera/Img_AccountType_01.png';
import ibaneraEmptyListImage from 'assets/ibanera/Img_List_Empty.png';
import ibaneraFillerImage from 'assets/ibanera/IbaneraBounce.gif';
import ibaneraIconClose from 'assets/ibanera/Icon_Close.png';
import syndicateFillerImage from 'assets/syndicate-icon.png';

/** Brand key derived from licenseesbranding `images.assetsPath` (e.g. `/assets/jhglobal`). */
export type BrandKey = 'jhglobal' | 'syndicate' | 'ibanera' | 'mbme' | 'switch' | 'default';

export type KnownBrandKey = Exclude<BrandKey, 'default'>;

/** Known `licenseesbranding` company names / aliases per client. */
export const BRAND_DISPLAY_NAMES: Record<KnownBrandKey, string> = {
    ibanera: 'Ibanera',
    jhglobal: '75 Pillars',
    syndicate: 'Syndicate Payments',
    mbme: 'MBME',
    switch: 'Switch',
};

const DEFAULT_ASSETS_PATH: Record<KnownBrandKey, string> = {
    ibanera: '/assets/ibanera',
    jhglobal: '/assets/jhglobal',
    syndicate: '/assets/syndicate',
    mbme: '/assets/mbme',
    switch: '/assets/switch',
};

const normalizeBrandToken = (value?: string | null): string =>
    value?.toLowerCase().replace(/[^a-z0-9]/g, '') ?? '';

export const getBrandKeyFromAssetsPath = (assetsPath?: string): BrandKey => {
    if (!assetsPath) {
        return 'default';
    }
    const segment = assetsPath.toLowerCase().split('/').filter(Boolean).pop() ?? '';
    if (segment.includes('jhglobal')) return 'jhglobal';
    if (segment.includes('syndicate')) return 'syndicate';
    if (segment.includes('ibanera')) return 'ibanera';
    if (segment.includes('mbme')) return 'mbme';
    if (segment === 'switch') return 'switch';
    return 'default';
};

/** Resolve brand from API `companyName` when assets path is missing or ambiguous. */
export const getBrandKeyFromCompanyName = (companyName?: string | null): KnownBrandKey | null => {
    const normalized = normalizeBrandToken(companyName);
    if (!normalized) {
        return null;
    }
    if (
        normalized.includes('jhglobal') ||
        normalized.includes('jhglobainvestments') ||
        normalized.includes('75pillars')
    ) {
        return 'jhglobal';
    }
    if (normalized.includes('ibanera')) {
        return 'ibanera';
    }
    if (normalized.includes('syndicate')) {
        return 'syndicate';
    }
    if (normalized.includes('mbme')) {
        return 'mbme';
    }
    if (normalized === 'switch' || normalized.includes('switchpayments')) {
        return 'switch';
    }
    return null;
};

/** Prefer assets path (production source of truth), then company name. */
export const getBrandKey = (assetsPath?: string, companyName?: string | null): BrandKey => {
    const fromPath = getBrandKeyFromAssetsPath(assetsPath);
    if (fromPath !== 'default') {
        return fromPath;
    }
    return getBrandKeyFromCompanyName(companyName) ?? 'default';
};

export const isIbaneraBrand = (assetsPath?: string, companyName?: string | null): boolean =>
    getBrandKey(assetsPath, companyName) === 'ibanera';

export const resolveAssetsPath = (
    assetsPath?: string,
    companyName?: string | null
): string | undefined => {
    if (assetsPath) {
        return assetsPath;
    }
    const brand = getBrandKeyFromCompanyName(companyName);
    return brand ? DEFAULT_ASSETS_PATH[brand] : undefined;
};

/** Bundled decorative images where a client has a dedicated PayIn / profile filler asset. */
const BUNDLED_BRAND_FILLER: Partial<Record<BrandKey, string>> = {
    ibanera: ibaneraFillerImage,
    syndicate: syndicateFillerImage,
};

/**
 * Side-panel filler image for account pages (PayIn, etc.).
 * Uses bundled assets for ibanera/syndicate; otherwise the licensee Logo from public/assets.
 */
export const getBrandFillerImage = (
    assetsPath?: string,
    companyName?: string | null
): string => {
    const brand = getBrandKey(assetsPath, companyName);
    const bundled = BUNDLED_BRAND_FILLER[brand];
    if (bundled) {
        return bundled;
    }
    const path = resolveAssetsPath(assetsPath, companyName);
    if (path) {
        return `${path}/Logo.png`;
    }
    return ibaneraFillerImage;
};

/** URL for a file under the active licensee `public/assets/{client}/` folder. */
export const getBrandAssetUrl = (
    assetsPath: string | undefined,
    companyName: string | null | undefined,
    fileName: string
): string => {
    const path = resolveAssetsPath(assetsPath, companyName);
    if (path) {
        return `${path}/${fileName}`;
    }
    return `${DEFAULT_ASSETS_PATH.ibanera}/${fileName}`;
};

export const getBrandAccountTypeImage = (
    assetsPath?: string,
    companyName?: string | null
): string => {
    const brand = getBrandKey(assetsPath, companyName);
    if (brand === 'ibanera') {
        return ibaneraAccountTypeImage;
    }
    return getBrandAssetUrl(assetsPath, companyName, 'Img_AccountType_01.png');
};

export const getBrandEmptyListImage = (
    assetsPath?: string,
    companyName?: string | null
): string => {
    const brand = getBrandKey(assetsPath, companyName);
    if (brand === 'ibanera') {
        return ibaneraEmptyListImage;
    }
    return getBrandAssetUrl(assetsPath, companyName, 'Img_List_Empty.png');
};

export const getBrandCloseIcon = (assetsPath?: string, companyName?: string | null): string => {
    // Client-specific public asset folders don't currently ship `Icon_Close.png`,
    // so always fall back to the bundled close icon to avoid broken modal headers.
    return ibaneraIconClose;
};

export const getHeaderClassName = (assetsPath?: string, companyName?: string | null): string => {
    const brand = getBrandKey(assetsPath, companyName);
    if (brand === 'jhglobal') return 'Header Header--jhglobal';
    if (brand === 'syndicate') return 'Header Header--syndicate';
    return 'Header';
};

export const getLogoClassName = (assetsPath?: string, companyName?: string | null): string => {
    const brand = getBrandKey(assetsPath, companyName);
    if (brand === 'jhglobal') return 'Logo Logo--jhglobal';
    if (brand === 'syndicate') return 'Logo Logo--syndicate';
    return 'Logo';
};

/** Sets `data-brand` on `<html>` so layout SCSS can match header sizing per client. */
export const applyBrandDocumentAttributes = (
    assetsPath?: string,
    companyName?: string | null
): void => {
    if (typeof document === 'undefined') {
        return;
    }
    const brand = getBrandKey(assetsPath, companyName);
    document.documentElement.dataset.brand = brand;
};
